import PageContainer from '@components/PageContainer';
import { useSendOtp, useVerifyOtp } from '@hooks/postQueries';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import { HorizontalRule } from '@mui/icons-material';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function VerifyOtp() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(120);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const purpose = location.state?.purpose;
  const { mutateAsync: verifyOtp, isPending: isVerifyOtpPending } = useVerifyOtp();
  const { mutateAsync: sendOtp } = useSendOtp();

  const handleChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newOtp.every((digit) => digit !== '') && index === 5) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length && i < 6; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }

    setOtp(newOtp);

    // Focus the next empty field or the last field
    const nextEmptyIndex = newOtp.findIndex((digit) => digit === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (otpCode) => {
    await verifyOtp(
      {
        email,
        otp: otpCode,
        purpose,
      },
      {
        onSuccess: () => {
          toast.success('OTP verified successfully!');
          // Navigate to create account or next step
          purpose === 'register_user'
            ? navigate('/signup/create-account', { state: { email, otp: otpCode } })
            : navigate('/reset-password', { state: { email, otp: otpCode } });
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Invalid OTP. Please try again.');
          setOtp(['', '', '', '', '', '']);
          inputRefs.current[0]?.focus();
        },
      },
    );
  };

  const startCountdown = useCallback(() => {
    setCountdown(120);
    setIsCountdownActive(true);
  }, []);

  // Start countdown on component mount if email exists (coming from previous step)
  useEffect(() => {
    if (email) {
      startCountdown();
    }
  }, [email, startCountdown]);

  useEffect(() => {
    let timer;
    if (isCountdownActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setIsCountdownActive(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, isCountdownActive]);

  const handleResend = async () => {
    if (isCountdownActive) return;

    await sendOtp(
      { email, purpose },
      {
        onSuccess: () => {
          toast.success('New OTP has been sent to your email!');
          inputRefs.current[0]?.focus();
          startCountdown();
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Failed to resend OTP. Please try again.');
        },
      },
    );
  };

  useEffect(() => {
    if (!email) {
      navigate('/signup');
      return;
    }
    inputRefs.current[0]?.focus();
  }, [email, navigate]);

  return (
    <PageContainer title="Qualz.ai | Verify OTP">
      <AuthFormLayout
        title="Verify OTP"
        text={`We sent an email to ${email}. Enter the code here or tap the button in the email to continue.`}
      >
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          {otp.map((digit, index) => (
            <React.Fragment key={index}>
              <TextField
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                data-testid="otp-input"
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                  },
                }}
                sx={{
                  width: 56,
                  '& .MuiOutlinedInput-root': {
                    height: 56,
                  },
                }}
                variant="outlined"
                disabled={isVerifyOtpPending}
              />
              {index === 2 && (
                <Box sx={{ mx: 1, display: 'flex', alignItems: 'center' }}>
                  <HorizontalRule />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Stack>

        <Button
          variant="contained"
          fullWidth
          size="large"
          data-testid="verify-button"
          onClick={() => handleVerify(otp.join(''))}
          disabled={otp.some((digit) => digit === '') || isVerifyOtpPending}
          sx={{ my: 2 }}
        >
          {isVerifyOtpPending ? 'Verifying...' : 'Verify OTP'}
        </Button>

        <Box sx={{ mt: 2, justifyContent: 'center' }}>
          <Typography variant="body2">Can’t find your code? - check your spam folder</Typography>
          <Typography
            variant="body2"
            onClick={!isCountdownActive ? handleResend : undefined}
            sx={{
              cursor: isCountdownActive ? 'default' : 'pointer',
              color: isCountdownActive ? 'text.secondary' : 'primary.main',
              ml: 0.5,
              mt: 0.5,
            }}
          >
            {isCountdownActive ? `Request new code in ${countdown}` : 'Request a new code.'}
          </Typography>
        </Box>
      </AuthFormLayout>
    </PageContainer>
  );
}

export default VerifyOtp;
